import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Dumbbell, Activity, TrendingUp, Trophy, Calendar, Target } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface ExerciseData {
  exercise_name: string;
  workout_date: string;
  weight_kg: number | null;
  sets: number | null;
  reps: number | null;
  distance_km: number | null;
  duration_seconds: number | null;
  workout_type: string;
}

interface PersonalRecord {
  type: string;
  value: number;
  date: string;
  unit: string;
}

interface ExerciseProgressModalProps {
  isOpen: boolean;
  onClose: () => void;
  exerciseName: string;
}

export const ExerciseProgressModal = ({ isOpen, onClose, exerciseName }: ExerciseProgressModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [exerciseData, setExerciseData] = useState<ExerciseData[]>([]);
  const [personalRecords, setPersonalRecords] = useState<PersonalRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && isOpen && exerciseName) {
      loadExerciseData();
    }
  }, [user, isOpen, exerciseName]);

  const loadExerciseData = async () => {
    if (!user || !exerciseName) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('exercises')
        .select(`
          name,
          sets,
          reps,
          weight_kg,
          distance_km,
          duration_seconds,
          workouts!inner(
            completed_at,
            workout_type,
            user_id
          )
        `)
        .eq('workouts.user_id', user.id)
        .eq('name', exerciseName)
        .not('workouts.completed_at', 'is', null)
        .order('completed_at', { referencedTable: 'workouts', ascending: true });

      if (error) throw error;

      const formattedData: ExerciseData[] = (data || []).map(exercise => ({
        exercise_name: exercise.name,
        workout_date: exercise.workouts.completed_at,
        weight_kg: exercise.weight_kg,
        sets: exercise.sets,
        reps: exercise.reps,
        distance_km: exercise.distance_km,
        duration_seconds: exercise.duration_seconds,
        workout_type: exercise.workouts.workout_type || 'strength'
      }));

      setExerciseData(formattedData);
      calculatePersonalRecords(formattedData);
    } catch (error) {
      console.error('Error loading exercise data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePersonalRecords = (data: ExerciseData[]) => {
    const records: PersonalRecord[] = [];

    // Calculate personal records based on exercise type
    const isRunning = data.some(d => d.workout_type === 'running' || d.exercise_name.toLowerCase().includes('running'));

    if (isRunning) {
      // Running records
      const maxDistance = Math.max(...data.filter(d => d.distance_km).map(d => d.distance_km!));
      const bestPace = Math.min(...data.filter(d => d.distance_km && d.duration_seconds).map(d => (d.duration_seconds! / 60) / d.distance_km!));
      
      if (maxDistance > 0) {
        const maxDistanceEntry = data.find(d => d.distance_km === maxDistance);
        records.push({
          type: 'Longest Distance',
          value: maxDistance,
          date: maxDistanceEntry!.workout_date,
          unit: 'km'
        });
      }

      if (bestPace && bestPace !== Infinity) {
        const bestPaceEntry = data.find(d => d.distance_km && d.duration_seconds && ((d.duration_seconds / 60) / d.distance_km) === bestPace);
        records.push({
          type: 'Best Pace',
          value: bestPace,
          date: bestPaceEntry!.workout_date,
          unit: 'min/km'
        });
      }
    } else {
      // Strength training records
      const maxWeight = Math.max(...data.filter(d => d.weight_kg).map(d => d.weight_kg!));
      const maxReps = Math.max(...data.filter(d => d.reps).map(d => d.reps!));
      const maxSets = Math.max(...data.filter(d => d.sets).map(d => d.sets!));

      if (maxWeight > 0) {
        const maxWeightEntry = data.find(d => d.weight_kg === maxWeight);
        records.push({
          type: 'Max Weight',
          value: maxWeight,
          date: maxWeightEntry!.workout_date,
          unit: 'kg'
        });
      }

      if (maxReps > 0) {
        const maxRepsEntry = data.find(d => d.reps === maxReps);
        records.push({
          type: 'Max Reps',
          value: maxReps,
          date: maxRepsEntry!.workout_date,
          unit: 'reps'
        });
      }

      if (maxSets > 0) {
        const maxSetsEntry = data.find(d => d.sets === maxSets);
        records.push({
          type: 'Max Sets',
          value: maxSets,
          date: maxSetsEntry!.workout_date,
          unit: 'sets'
        });
      }
    }

    setPersonalRecords(records);
  };

  const getProgressData = () => {
    return exerciseData.map((d, index) => ({
      session: index + 1,
      date: new Date(d.workout_date).toLocaleDateString(),
      weight: d.weight_kg || 0,
      sets: d.sets || 0,
      reps: d.reps || 0,
      distance: d.distance_km || 0,
      pace: d.distance_km && d.duration_seconds 
        ? (d.duration_seconds / 60) / d.distance_km 
        : 0,
      workout_type: d.workout_type
    }));
  };

  const progressData = getProgressData();
  const isRunningExercise = progressData.length > 0 && 
    (progressData[0].workout_type === 'running' || exerciseName.toLowerCase().includes('running'));

  const recordTypeLabel = (type: string): string => {
    const map: Record<string, string> = {
      'Longest Distance': t('components.exerciseProgressModal.longestDistance'),
      'Best Pace': t('components.exerciseProgressModal.bestPace'),
      'Max Weight': t('components.exerciseProgressModal.maxWeight'),
      'Max Reps': t('components.exerciseProgressModal.maxReps'),
      'Max Sets': t('components.exerciseProgressModal.maxSets'),
    };
    return map[type] ?? type;
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <div className="text-center py-8 text-muted-foreground">
            {t('components.exerciseProgressModal.loading')}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-6xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            <span className="truncate">{exerciseName}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-6">
          {/* Personal Records */}
          <Card className="bg-gradient-card border-0">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500" />
                {t('components.exerciseProgressModal.personalRecords')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              {personalRecords.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  {personalRecords.map((record, index) => (
                    <div key={index} className="text-center p-3 sm:p-4 bg-background/20 rounded-lg border border-border/30">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Target className="h-3 w-3 sm:h-4 sm:w-4 text-primary" />
                        <span className="font-semibold text-xs sm:text-sm">{recordTypeLabel(record.type)}</span>
                      </div>
                      <div className="text-xl sm:text-2xl font-bold text-primary mb-1">
                        {record.unit === 'min/km' ? record.value.toFixed(2) : record.value}
                        <span className="text-xs sm:text-sm text-muted-foreground ml-1">{record.unit}</span>
                      </div>
                      <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {new Date(record.date).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  <Trophy className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>{t('components.exerciseProgressModal.noRecords')}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Progress Charts */}
          <Card className="bg-gradient-card border-0">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                <span className="text-sm sm:text-base">{t('components.exerciseProgressModal.progress', { count: progressData.length })}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0">
              {progressData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>{t('components.exerciseProgressModal.noProgress')}</p>
                </div>
              ) : isRunningExercise ? (
                <Tabs defaultValue="distance" className="space-y-4">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="distance" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                      <Activity className="h-3 w-3 sm:h-4 sm:w-4" />
                      {t('components.exerciseProgressModal.tabDistance')}
                    </TabsTrigger>
                    <TabsTrigger value="pace" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                      <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
                      {t('components.exerciseProgressModal.tabPace')}
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="distance">
                    <div className="h-64 sm:h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={progressData}>
                          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                          <XAxis 
                            dataKey="session" 
                            label={{ value: t('components.exerciseProgressModal.axisSession'), position: 'insideBottom', offset: -10 }}
                          />
                          <YAxis 
                            label={{ value: t('components.exerciseProgressModal.axisDistanceKm'), angle: -90, position: 'insideLeft' }}
                          />
                          <Tooltip 
                            labelFormatter={(label) => t('components.exerciseProgressModal.tooltipSession', { number: label })}
                            formatter={(value: number) => [`${value.toFixed(1)} km`, t('components.exerciseProgressModal.tooltipDistance')]}
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="distance" 
                            stroke="hsl(var(--accent))" 
                            strokeWidth={3}
                            dot={false}
                            activeDot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </TabsContent>

                  <TabsContent value="pace">
                    <div className="h-64 sm:h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={progressData}>
                          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                          <XAxis 
                            dataKey="session" 
                            label={{ value: t('components.exerciseProgressModal.axisSession'), position: 'insideBottom', offset: -10 }}
                          />
                          <YAxis 
                            label={{ value: t('components.exerciseProgressModal.axisPaceMinKm'), angle: -90, position: 'insideLeft' }}
                          />
                          <Tooltip 
                            labelFormatter={(label) => t('components.exerciseProgressModal.tooltipSession', { number: label })}
                            formatter={(value: number) => [`${value.toFixed(2)} min/km`, t('components.exerciseProgressModal.tooltipPace')]}
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="pace" 
                            stroke="hsl(var(--secondary))" 
                            strokeWidth={3}
                            dot={false}
                            activeDot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </TabsContent>
                </Tabs>
              ) : (
                <Tabs defaultValue="weight" className="space-y-4">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="weight" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                      <Dumbbell className="h-3 w-3 sm:h-4 sm:w-4" />
                      <span className="hidden xs:inline">{t('components.exerciseProgressModal.tabWeight')}</span>
                      <span className="xs:hidden">{t('components.exerciseProgressModal.tabWeightShort')}</span>
                    </TabsTrigger>
                    <TabsTrigger value="sets" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                      <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
                      {t('components.exerciseProgressModal.tabSets')}
                    </TabsTrigger>
                    <TabsTrigger value="reps" className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm">
                      <Activity className="h-3 w-3 sm:h-4 sm:w-4" />
                      {t('components.exerciseProgressModal.tabReps')}
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="weight">
                    <div className="h-64 sm:h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={progressData}>
                          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                          <XAxis 
                            dataKey="session" 
                            label={{ value: t('components.exerciseProgressModal.axisSession'), position: 'insideBottom', offset: -10 }}
                          />
                          <YAxis 
                            label={{ value: t('components.exerciseProgressModal.axisWeightKg'), angle: -90, position: 'insideLeft' }}
                          />
                          <Tooltip 
                            labelFormatter={(label) => t('components.exerciseProgressModal.tooltipSession', { number: label })}
                            formatter={(value: number) => [`${value} kg`, t('components.exerciseProgressModal.tooltipWeight')]}
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="weight" 
                            stroke="hsl(var(--primary))" 
                            strokeWidth={3}
                            dot={false}
                            activeDot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </TabsContent>

                  <TabsContent value="sets">
                    <div className="h-64 sm:h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={progressData}>
                          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                          <XAxis 
                            dataKey="session" 
                            label={{ value: t('components.exerciseProgressModal.axisSession'), position: 'insideBottom', offset: -10 }}
                          />
                          <YAxis 
                            label={{ value: t('components.exerciseProgressModal.axisSets'), angle: -90, position: 'insideLeft' }}
                          />
                          <Tooltip 
                            labelFormatter={(label) => t('components.exerciseProgressModal.tooltipSession', { number: label })}
                            formatter={(value: number) => [`${value}`, t('components.exerciseProgressModal.tooltipSets')]}
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Bar 
                            dataKey="sets" 
                            fill="hsl(var(--secondary))" 
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </TabsContent>

                  <TabsContent value="reps">
                    <div className="h-64 sm:h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={progressData}>
                          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                          <XAxis 
                            dataKey="session" 
                            label={{ value: t('components.exerciseProgressModal.axisSession'), position: 'insideBottom', offset: -10 }}
                          />
                          <YAxis 
                            label={{ value: t('components.exerciseProgressModal.axisReps'), angle: -90, position: 'insideLeft' }}
                          />
                          <Tooltip 
                            labelFormatter={(label) => t('components.exerciseProgressModal.tooltipSession', { number: label })}
                            formatter={(value: number) => [`${value}`, t('components.exerciseProgressModal.tooltipReps')]}
                            contentStyle={{
                              backgroundColor: 'hsl(var(--card))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Bar 
                            dataKey="reps" 
                            fill="hsl(var(--accent))" 
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};